import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { AppTheme } from '@/shared/constant/AppTheme';
import { LocalStorageKey } from '@/shared/constant/local-storage/LocalStorageKey';
import { gaReport } from '@/shared/util/analytics';
import clsx from 'clsx';
import React from 'react';
import styles from './ThemeSwitch.module.css';

export type ThemeSwitchProps = {
  className?: string;
  idPrefix?: string;
};

export function ThemeSwitch(props: ThemeSwitchProps) {
  const { className } = props;

  const [hasAnimation, setHasAnimation] = React.useState(false);
  const onlineTheme = useDocsDataModelStore((x) => x.docsBaseConfig.theme);
  const appTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const setAppTheme = useAppearanceConfigModelStore((x) => x.setAppTheme);
  const themePrimarySettings = useDocsDataModelStore((x) => x.docsBaseConfig.themePrimarySettings);
  const logoSettings = useDocsDataModelStore((x) => x.docsBaseConfig.logoSettings);
  const displayThemeToggle = useDocsDataModelStore((x) => x.docsBaseConfig.displayThemeToggle);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const id = useDocsDataModelStore((x) => x.docsIdTypeData.onlineId);
  const idPrefix = React.useId();

  React.useEffect(() => {
    setHasAnimation(true);
  }, []);

  const switchThemeFn = usePersistFn((newAppTheme: AppTheme.Light | AppTheme.Dark) => {
    localStorage.setItem(`${LocalStorageKey.OnlineTheme}.${id}`, onlineTheme);
    localStorage.setItem(`${LocalStorageKey.UserTheme}.${id}`, newAppTheme);
    window.__setDocsTheme(themePrimarySettings, logoSettings, newAppTheme);
    setAppTheme(newAppTheme);
    gaReport(DOCS_SITE_EVENT.SWITCH_CLICK_BACKGROUND, { page_title: title });
  });

  if (!displayThemeToggle) {
    return null;
  }
  return (
    <div
      className={clsx(
        'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg',
        {
          [`${styles.container}`]: true,
          [`${styles['container-animation']}`]: hasAnimation,
        },
        className,
      )}
      onClick={() => {
        const newAppTheme = appTheme === AppTheme.Light ? AppTheme.Dark : AppTheme.Light;
        switchThemeFn(newAppTheme);
      }}
    >
      <button
        className={clsx('theme-toggle', styles['expand-button'], 'flex items-center', {
          'theme-toggle--dark': appTheme === AppTheme.Dark,
        })}
        aria-label="Toggle theme"
      >
        {
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            fill="currentColor"
            aria-hidden="true"
            className="theme-toggle-svg"
            viewBox="0 0 24 24"
          >
            <clipPath id={`${idPrefix || ''}a`}>
              <path d="M0-11h19A1 1 0 0 0 36 2v22H0Z" />
            </clipPath>
            <g clipPath={`url(#${idPrefix || ''}a)`}>
              <circle cx={12} cy={12} r={6.4} />
              <path d="M13.7 2.7c0 1-.7 1.8-1.7 1.8s-1.8-.8-1.8-1.8S11 1 12 1s1.7.7 1.7 1.7zm-3.5 19.5c0-1 .8-1.7 1.8-1.7s1.6.6 1.6 1.6-.7 1.8-1.7 1.8-1.8-.8-1.8-1.8zm11.5-8c-1 0-1.7-.7-1.7-1.7s.7-1.8 1.7-1.8 1.8.8 1.8 1.8-.9 1.6-1.9 1.6zM2.2 10.7c1 0 1.8.8 1.8 1.8s-.8 1.7-1.8 1.7-1.7-.7-1.7-1.7.7-1.8 1.7-1.8zm4.4-5.3c0 1-.8 1.7-1.7 1.7-1 0-1.8-.7-1.8-1.7s.8-1.8 1.8-1.8 1.7.8 1.7 1.8zm12.5 16c-1 0-1.8-.8-1.8-1.8s.8-1.7 1.8-1.7 1.7.7 1.7 1.7-.8 1.8-1.7 1.8zm1.8-16c0 1-.8 1.7-1.8 1.7s-1.8-.8-1.8-1.7c0-1 .8-1.8 1.8-1.8s1.8.8 1.8 1.8zm-16 12.4c1 0 1.7.8 1.7 1.8s-.7 1.7-1.7 1.7-1.8-.8-1.8-1.7c0-1 .8-1.8 1.8-1.8z" />
            </g>
          </svg>
        }
      </button>
    </div>
  );
}
